/**
 * « Faire payer par un proche » (migration 0312) — `/payer/$token`.
 *
 * Page PUBLIQUE ouverte par la personne à qui l'élève a envoyé le lien
 * (parent, proche, ami) : ce qu'il demande, le prix actuel (cartes déjà dans
 * son deck déduites), puis paiement Stripe SANS compte. Au retour (`?paid=1`)
 * on attend la confirmation du webhook ; l'élève est prévenu par notification.
 */
import { useEffect, useState } from 'react';
import { useParams, useSearch } from '@tanstack/react-router';
import { CheckCircle2, Gift, Loader2, ShieldCheck } from 'lucide-react';
import { Button, MathText } from '@jeraisonne/ui';
import { SCHOOL_LEVELS, SUBJECTS } from '@jeraisonne/db';
import { formatEuros } from '@jeraisonne/db/cards';
import { usePublicCard } from '@/lib/cards/api';
import {
  giftCheckoutErrorMessage,
  startGiftCheckout,
  usePayRequest,
  type PayRequestQuote,
} from '@/lib/skillcards/pay-request';
import { SkillcardBuyHero } from '@/features/skillcards/skillcard-buy-hero';

const LEVEL_LABELS: Record<string, string> = Object.fromEntries(
  SCHOOL_LEVELS.map((l) => [l.value, l.label]),
);
const SUBJECT_LABELS: Record<string, string> = Object.fromEntries(
  SUBJECTS.map((s) => [s.value, s.label]),
);

/** « 7 cartes · 5ème · Mathématiques » / « 1 SKILLCARD ». */
export function payOfferSubtitle(q: PayRequestQuote): string {
  const what =
    q.kind === 'card' ? '1 SKILLCARD' : `${q.cards_to_buy} carte${q.cards_to_buy > 1 ? 's' : ''}`;
  return [
    what,
    q.level && (LEVEL_LABELS[q.level] ?? q.level),
    q.subject && (SUBJECT_LABELS[q.subject] ?? q.subject),
  ]
    .filter(Boolean)
    .join(' · ');
}

export function PayForStudentPage() {
  const { token } = useParams({ strict: false }) as { token: string };
  const search = useSearch({ strict: false }) as { paid?: string };
  const justPaid = search.paid === '1';
  const request = usePayRequest(token, { poll: justPaid });
  const publicCard = usePublicCard(request.data?.card_id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  const data = request.data;
  useEffect(() => {
    if (data) document.title = `Offrir des SKILLCARDS à ${data.first_name} · JeRaisonne`;
  }, [data]);
  useEffect(() => {
    if (!justPaid) return;
    const t = setTimeout(() => setTimedOut(true), 60_000);
    return () => clearTimeout(t);
  }, [justPaid]);

  async function pay() {
    setBusy(true);
    setError(null);
    try {
      window.location.href = await startGiftCheckout(token);
    } catch (e) {
      setError(giftCheckoutErrorMessage((e as Error).message));
      setBusy(false);
    }
  }

  return (
    <div className="native:pt-[calc(env(safe-area-inset-top)+0.75rem)] min-h-dvh px-4 pb-[max(env(safe-area-inset-bottom),1.5rem)] pt-6 sm:py-8">
      <div className="mx-auto max-w-xl space-y-4">
        {request.isLoading ? (
          <div className="bg-ink-100 h-40 animate-pulse rounded-2xl" />
        ) : !data ? (
          <Message title="Lien introuvable">
            Ce lien n’existe pas ou a été mal copié. Demande à l’élève de te le renvoyer.
          </Message>
        ) : data.status === 'paid' ? (
          <div className="space-y-3 text-center">
            <CheckCircle2 className="text-success-500 mx-auto h-12 w-12" aria-hidden />
            <h1 className="font-display text-ink-900 text-2xl font-extrabold">
              {justPaid ? 'Merci !' : 'C’est déjà payé'}
            </h1>
            <p className="text-ink-600 text-sm">
              Les SKILLCARDS sont dans le deck de {data.first_name}, qui vient d’être prévenu(e).
            </p>
          </div>
        ) : justPaid ? (
          <div className="space-y-3 text-center">
            <Loader2 className="text-primary-500 mx-auto h-10 w-10 animate-spin" aria-hidden />
            <h1 className="font-display text-ink-900 text-xl font-extrabold">
              On confirme ton paiement…
            </h1>
            {timedOut && (
              <p className="text-ink-600 text-sm">
                C’est plus long que prévu. Ton paiement est bien pris en compte : {data.first_name}{' '}
                sera prévenu(e) dès qu’il est confirmé.
              </p>
            )}
          </div>
        ) : data.status === 'expired' || data.status === 'cancelled' ? (
          <Message title="Ce lien n’est plus valable">
            Demande à {data.first_name} de t’en renvoyer un depuis son application.
          </Message>
        ) : data.status === 'owned' ? (
          <Message title="Rien à payer">
            Ces SKILLCARDS sont déjà dans le deck de {data.first_name}.
          </Message>
        ) : data.status === 'unavailable' || !data.quote ? (
          <Message title="Offre indisponible">
            Cette offre n’est plus en vente. Demande à {data.first_name} de t’envoyer un nouveau
            lien.
          </Message>
        ) : (
          <>
            <div className="flex items-start gap-3">
              <Gift className="text-accent-500 mt-1 h-6 w-6 shrink-0" aria-hidden />
              <div>
                <h1 className="font-display text-ink-900 text-2xl font-extrabold">
                  {data.first_name} te demande de lui offrir des SKILLCARDS
                </h1>
                <p className="text-ink-600 mt-1 text-sm">
                  Des cartes de méthode pour progresser, avec leurs exercices, sur JeRaisonne.
                </p>
              </div>
            </div>

            {publicCard.data && data.kind === 'card' && (
              <SkillcardBuyHero
                title={publicCard.data.card.title}
                levels={publicCard.data.card.levels}
                colorTheme={publicCard.data.card.color_theme}
                creature={publicCard.data.card.creature}
                xpMax={publicCard.data.card.xp_max}
              />
            )}

            <div className="border-ink-200 space-y-4 rounded-2xl border bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-ink-900 font-display text-base font-bold">
                    <MathText>{data.quote.label}</MathText>
                  </p>
                  <p className="text-ink-500 text-sm">{payOfferSubtitle(data.quote)}</p>
                </div>
                <p className="text-ink-900 font-display shrink-0 text-3xl font-extrabold leading-none">
                  {formatEuros(data.quote.price_cents)}
                </p>
              </div>
              <Button fullWidth disabled={busy} onClick={() => void pay()}>
                {busy
                  ? 'Redirection…'
                  : `Payer ${formatEuros(data.quote.price_cents)} pour ${data.first_name}`}
              </Button>
              {error && (
                <p role="alert" className="text-error-600 text-sm">
                  {error}
                </p>
              )}
            </div>

            <p className="text-ink-500 flex items-start gap-2 text-xs leading-relaxed">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              Paiement unique sécurisé par Stripe, sans abonnement et sans créer de compte.{' '}
              {data.first_name} est prévenu(e) dès que c’est payé ; les cartes restent actives 1 an
              dans son deck.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function Message({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2 pt-8 text-center">
      <h1 className="font-display text-ink-900 text-xl font-extrabold">{title}</h1>
      <p className="text-ink-600 text-sm">{children}</p>
    </div>
  );
}
